import { useRef, useCallback, useLayoutEffect } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import SuperscriptIcon from '@mui/icons-material/Superscript';
import SubscriptIcon from '@mui/icons-material/Subscript';
import FormatClearIcon from '@mui/icons-material/FormatClear';
import { GOLD } from '../theme/tokens';

const ALLOWED_TAGS = new Set([
    'b', 'strong', 'i', 'em', 'u', 'sub', 'sup', 's', 'strike', 'del', 'br',
    'p', 'span', 'div',
]);

function sanitizeHtml(html: string): string {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const clean = (node: Node): string => {
        if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? '';
        if (node.nodeType !== Node.ELEMENT_NODE) return '';
        const el = node as Element;
        const tag = el.tagName.toLowerCase();
        const children = Array.from(el.childNodes).map(clean).join('');
        if (ALLOWED_TAGS.has(tag)) return `<${tag}>${children}</${tag}>`;
        return children;
    };
    return Array.from(doc.body.childNodes).map(clean).join('');
}

interface SimpleHtmlEditorProps {
    value: string;
    onChange: (html: string) => void;
    placeholder?: string;
    minHeight?: number;
}

export default function SimpleHtmlEditor({
    value,
    onChange,
    placeholder = 'Enter description...',
    minHeight = 100,
}: SimpleHtmlEditorProps) {
    const editorRef = useRef<HTMLDivElement>(null);
    const internalChange = useRef(false);

    // Set innerHTML imperatively so React never overwrites it mid-edit.
    useLayoutEffect(() => {
        const el = editorRef.current;
        if (!el) return;
        if (internalChange.current) {
            internalChange.current = false;
            return;
        }
        if (el.innerHTML !== value) {
            el.innerHTML = value;
        }
    }, [value]);

    const exec = useCallback((command: string) => {
        editorRef.current?.focus();
        document.execCommand(command, false);
        if (editorRef.current) {
            onChange(sanitizeHtml(editorRef.current.innerHTML));
        }
    }, [onChange]);

    const handleInput = useCallback(() => {
        if (editorRef.current) {
            internalChange.current = true;
            const html = editorRef.current.innerHTML;
            const isEmpty = html === '<br>' || html === '<div><br></div>' || html.trim() === '';
            onChange(isEmpty ? '' : sanitizeHtml(html));
        }
    }, [onChange]);

    const handlePaste = useCallback((e: React.ClipboardEvent) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain');
        document.execCommand('insertText', false, text);
    }, []);

    const buttons = [
        { command: 'bold', icon: <FormatBoldIcon fontSize="small" />, label: 'Bold' },
        { command: 'italic', icon: <FormatItalicIcon fontSize="small" />, label: 'Italic' },
        { command: 'underline', icon: <FormatUnderlinedIcon fontSize="small" />, label: 'Underline' },
        { command: 'strikethrough', icon: <StrikethroughSIcon fontSize="small" />, label: 'Strikethrough' },
        { command: 'superscript', icon: <SuperscriptIcon fontSize="small" />, label: 'Superscript' },
        { command: 'subscript', icon: <SubscriptIcon fontSize="small" />, label: 'Subscript' },
        { command: 'removeFormat', icon: <FormatClearIcon fontSize="small" />, label: 'Clear Formatting' },
    ];

    const showPlaceholder = !value;

    return (
        <Box sx={{
            border: '1px solid #CCCCCC',
            borderRadius: 1,
            overflow: 'hidden',
            bgcolor: '#FFFFFF',
            '&:focus-within': { borderColor: GOLD, borderWidth: 2 },
        }}>
            {/* Toolbar */}
            <Box sx={{
                display: 'flex',
                gap: 0.25,
                px: 0.5,
                py: 0.25,
                borderBottom: '1px solid #EEEEEE',
                bgcolor: '#F9F9F9',
            }}>
                {buttons.map(btn => (
                    <Tooltip key={btn.command} title={btn.label} arrow>
                        <IconButton
                            size="small"
                            onMouseDown={e => { e.preventDefault(); exec(btn.command); }}
                            sx={{ color: '#555', '&:hover': { color: GOLD } }}
                        >
                            {btn.icon}
                        </IconButton>
                    </Tooltip>
                ))}
            </Box>

            {/* Editable area */}
            <Box
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                onInput={handleInput}
                onPaste={handlePaste}
                sx={{
                    minHeight,
                    px: 1.5,
                    py: 1,
                    color: '#333333',
                    fontSize: '0.875rem',
                    lineHeight: 1.6,
                    outline: 'none',
                    '&:empty::before': {
                        content: showPlaceholder ? `"${placeholder}"` : '""',
                        color: '#AAAAAA',
                    },
                }}
            />
        </Box>
    );
}
